import { useCallback } from 'react'
import { CustomerLogo } from '@sequencehq/core-components'
import type { PaginatedResult } from '@sequencehq/core-components'
import { fetchQuery } from '@sequencehq/api'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { MINUTES } from '@sequencehq/utils/dates'
import { getLogoSrc } from 'Customer/utils/logo'

export type CustomerItem =
  DashboardApi20240730.GetCustomers.CustomersList['items'][number]

const PAGE_SIZE = 100

export const useCustomerPaginatedFetcher = () => {
  const paginatedFetcher = useCallback(
    async (
      searchTerm: string,
      cursor?: string
    ): Promise<PaginatedResult<CustomerItem>> => {
      const data = await fetchQuery(
        apiQueryClient,
        dashboard20240730Client.getCustomers,
        {
          legalName: searchTerm || undefined,
          limit: PAGE_SIZE,
          sortBy: 'legalName' as const,
          sortOrder: 'ASC' as const,
          after: cursor
        },
        { staleTime: 1 * MINUTES }
      )

      const items = data?.items ?? []
      const pagination = data?.pagination

      return {
        items,
        cursor: pagination?.after,
        hasMore: items.length === PAGE_SIZE && Boolean(pagination?.after)
      }
    },
    []
  )

  const mapToOption = useCallback(
    (customer: CustomerItem) => ({
      label: customer.legalName,
      value: customer.id,
      icon: <CustomerLogo src={getLogoSrc(customer)} />
    }),
    []
  )

  // Resolves a set of customer ids to options by id. Used by the async
  // multi-select to render the labels of already-selected customers even when
  // they fall outside the current search results.
  const resolveValues = useCallback(async (ids: string[]) => {
    // Resolve each id independently so one failed lookup doesn't drop the
    // labels for the customers that did resolve.
    const results = await Promise.allSettled(
      ids.map(id =>
        fetchQuery(
          apiQueryClient,
          dashboard20240730Client.getCustomer,
          { id },
          { staleTime: 1 * MINUTES }
        )
      )
    )

    return results
      .flatMap(result =>
        result.status === 'fulfilled' && result.value ? [result.value] : []
      )
      .map(customer => ({
        label: customer.legalName,
        value: customer.id,
        icon: <CustomerLogo src={getLogoSrc(customer)} />
      }))
  }, [])

  return { paginatedFetcher, mapToOption, resolveValues }
}
